import posthog from 'posthog-js';
import { getConfig } from '../config/config';
import { getCurrentIdToken } from '../utils/firebase';
import { POSTHOG_FEATURE_FLAGS } from '../components/trashie/config/config';
import safeStorage from '../components/trashie/utils/localStorage';

const REACT_APP_API_ENDPOINT = getConfig('REACT_APP_CLOUD_API_ENDPOINT');

// Capture native fetch before any third-party scripts (e.g. GTM pixels) can
// monkey-patch window.fetch with a broken wrapper that corrupts Request objects.
const nativeFetch = window.fetch.bind(window);

const TOKEN_REFRESHED_KEY = 'token_refreshed';

export const apiFetch = async ({ path, body = undefined, method = 'GET', cacheControl, authToken }) => {
  const methodToUse = method || (body ? 'POST' : 'GET');
  const token = authToken || await getCurrentIdToken();
  const posthogSessionId = posthog?.get_session_id();
  const rewardsV2Enabled =
  (posthog?.featureFlags?.getFeatureFlag(POSTHOG_FEATURE_FLAGS.REWARDS_V2_AB)
    === POSTHOG_FEATURE_FLAGS.REWARDS_V2);
  const tokenRefreshed = safeStorage.getItem(TOKEN_REFRESHED_KEY) === 'true';

  const headers = {
    authorization: `Bearer ${token}`,
    'Content-Length': body ? body?.length?.toString() : undefined,
    'Content-Type': body ? 'application/json' : undefined,
    ...(cacheControl && { 'Cache-Control': cacheControl }),
    ...(posthogSessionId && { 'X-PostHog-Session-ID': posthogSessionId }),
    ...(rewardsV2Enabled && { 'X-Rewards-V2': rewardsV2Enabled }),
    ...(tokenRefreshed && { 'X-Token-Refreshed': 'true' }),
  };

  try {
    const res = await nativeFetch(REACT_APP_API_ENDPOINT + path, {
      method: methodToUse,
      body,
      headers,
    });

    const result = await res.json();
    if (res.status > 202) {
      const error = { error: true, ...result };
      throw error;
    }
    return result;
  } finally {
    // Clear after the first attempt regardless of success or failure,
    // so the header is never sent more than once.
    if (tokenRefreshed) {
      safeStorage.removeItem(TOKEN_REFRESHED_KEY);
    }
  }
};

const get = async ({ path, options, cacheControl }) => {
  const queryString = new URLSearchParams({ options: JSON.stringify(options) }).toString();
  const pathString = options ? `${path}?${queryString}` : path;

  const { data } = await apiFetch({ path: pathString, cacheControl });
  return data;
};

const post = async ({ path, body }) => {
  const { data } = await apiFetch({ path, body: JSON.stringify(body), method: 'POST' });
  return data;
};

const patch = async ({ path, body }) => {
  const { data } = await apiFetch({ path, body: JSON.stringify(body), method: 'PATCH' });
  return data;
};

const deleteFunc = async ({ path }) => {
  const { data } = await apiFetch({ path, method: 'DELETE' });
  return data;
};

export const markTokenAsRefreshed = () => {
  safeStorage.setItem(TOKEN_REFRESHED_KEY, 'true');
};

export default {
  get,
  post,
  patch,
  delete: deleteFunc,
};
